import { Link } from "react-router-dom";
import { ArrowUpRight, Kanban, Lightning, SealCheck, PaperPlaneTilt, Globe, EnvelopeSimple, Code } from "@phosphor-icons/react";
import { TOOLS } from "../lib/tools";
import { useAuth } from "../contexts/AuthContext";

function normalizePlan(plan) {
  const value = String(plan || "starter").trim().toLowerCase();
  return value === "free" ? "starter" : value;
}

function UsageOverview({ usage }) {
  if (!usage) return null;

  const isUnlimited = Boolean(usage.is_unlimited || usage.cap === null);
  const addonRemaining = Number(usage.addon_credits?.remaining || 0);
  const totalAvailable = isUnlimited ? "∞" : Number(usage.total_available ?? 0);
  const baseRemaining = isUnlimited ? "∞" : Number(usage.remaining || 0);

  return (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-usage-overview">
      <div className="border border-[#E5E7EB] bg-white p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Plan</p>
        <p className="font-display text-2xl font-black mt-1 capitalize">{normalizePlan(usage.plan)}</p>
      </div>
      <div className="border border-[#E5E7EB] bg-white p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Used this month</p>
        <p className="font-display text-2xl font-black mt-1">{Number(usage.used || 0)}{!isUnlimited && <span className="text-sm text-[#737373]"> / {usage.cap}</span>}</p>
      </div>
      <div className="border border-[#E5E7EB] bg-white p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Plan remaining</p>
        <p className="font-display text-2xl font-black mt-1">{baseRemaining}</p>
      </div>
      <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1D4ED8]">Available now</p>
        <p className="font-display text-2xl font-black mt-1 text-[#002FA7]">{totalAvailable}</p>
        {!isUnlimited && addonRemaining > 0 && <p className="text-xs text-[#1E40AF] mt-1">Includes {addonRemaining} add-on credits</p>}
      </div>
    </div>
  );
}

function ProCommandCenter({ usage }) {
  const plan = normalizePlan(usage?.plan);
  if (plan !== "pro") return null;

  const cards = [
    { icon: Lightning, title: "Unlimited generations", text: "No add-ons needed on Pro." },
    { icon: Kanban, title: "Workflow automation", text: "Recurring AI runs, approvals, and action tasks." },
    { icon: PaperPlaneTilt, title: "LinkedIn publishing", text: "Preview and publish approved posts." },
    { icon: Globe, title: "WordPress drafts", text: "Send approved content to your blog as drafts." },
    { icon: EnvelopeSimple, title: "Newsletter export", text: "Generate HTML, subject, preview, and plain text." },
    { icon: Code, title: "API access", text: "Use Ordeneli from your own systems." },
  ];

  return (
    <section className="container-wide py-8 md:py-12 border-b border-[#E5E7EB]" data-testid="pro-command-center">
      <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-white p-6 sm:p-8">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-white/60">Pro command center</p>
        <div className="mt-2 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Automation, publishing, and approvals in one place.</h2>
            <p className="mt-3 text-white/70 max-w-2xl">Pro turns Ordeneli from a tool library into a marketing operating system.</p>
          </div>
          <Link to="/workflow" className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-4 py-3 text-sm font-bold hover:bg-[#F5F5F5]">
            Open workflow center <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {cards.map(({ icon: Icon, title, text }) => (
            <div key={title} className="border border-white/15 bg-white/5 p-4">
              <Icon size={22} weight="duotone" className="text-white" />
              <p className="font-display text-lg font-bold mt-3">{title}</p>
              <p className="text-sm text-white/60 mt-1">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { usage } = useAuth();
  const groups = TOOLS.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  const plan = normalizePlan(usage?.plan);

  return (
    <div>
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
            Studio
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Pick a tool to get started.
          </h1>
          <p className="mt-4 text-[#525252] max-w-xl text-sm sm:text-base">
            Ten specialized AI tools. Select one, fill in the inputs, and generate production-ready output.
          </p>

          <UsageOverview usage={usage} />

          {usage && !usage.is_unlimited && Number(usage.total_available || 0) <= 0 && (
            <div className="mt-5 border border-[#DC2626] bg-[#FEF2F2] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="dashboard-out-of-credits">
              <div>
                <p className="font-semibold text-[#B91C1C]">You're out of available generations.</p>
                <p className="text-sm text-[#525252] mt-1">Buy a one-off add-on pack or upgrade your plan to continue.</p>
              </div>
              <Link to="/pricing" className="btn-primary">Buy credits</Link>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 items-center text-xs font-mono uppercase tracking-[0.18em] text-[#737373]" data-testid="plan-legend">
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center px-1.5 py-0.5 bg-[#002FA7] text-white font-bold text-[9px] tracking-[0.14em]">Free</span>
              SEO AI · Business Starter Kit
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center px-1.5 py-0.5 border border-[#0A0A0A] text-[#0A0A0A] font-bold text-[9px] tracking-[0.14em]">Growth</span>
              Premium tools and higher limits
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center px-1.5 py-0.5 bg-[#0A0A0A] text-white font-bold text-[9px] tracking-[0.14em]">Pro</span>
              Automation, publishing, API
            </span>
          </div>
          <Link to="/workflow" className="mt-7 inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-3 text-sm font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="dashboard-workflow-link">
            <Kanban size={18} weight="duotone" /> Open workflow center <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <ProCommandCenter usage={usage} />

      {Object.entries(groups).map(([group, tools]) => (
        <section key={group} className="container-wide py-8 md:py-12 border-b border-[#E5E7EB] last:border-0">
          <div className="flex items-center justify-between mb-5 md:mb-6">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
              — {group}
            </p>
            <span className="font-mono text-xs text-[#737373]">{tools.length} tools</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {tools.map((t) => {
              const Icon = t.icon;
              const paidLabel = plan === "pro" ? "Pro" : "Growth";
              return (
                <Link
                  key={t.key}
                  to={`/tools/${t.key}`}
                  className="group bg-white border border-[#E5E7EB] p-5 flex flex-col gap-3 hover:border-[#0A0A0A] transition-colors"
                  data-testid={`dashboard-tool-${t.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="h-9 w-9 shrink-0 bg-[#0A0A0A] text-white flex items-center justify-center group-hover:bg-[#002FA7] transition-colors">
                      <Icon size={18} weight="duotone" />
                    </div>

                    <div className="flex items-center justify-end gap-2 min-h-[24px] shrink-0">
                      {plan === "pro" && !t.free && (
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center border border-[#16A34A] bg-[#ECFDF5] text-[#16A34A]"
                          title="Included in your Pro plan"
                          aria-label="Included in your Pro plan"
                        >
                          <SealCheck size={16} weight="duotone" />
                        </span>
                      )}

                      {t.free ? (
                        <span className="inline-flex h-6 items-center px-2 bg-[#002FA7] text-white font-mono text-[9px] font-bold uppercase tracking-[0.14em]">Free</span>
                      ) : (
                        <span className="inline-flex h-6 items-center px-2 border border-[#0A0A0A] text-[#0A0A0A] font-mono text-[9px] font-bold uppercase tracking-[0.14em]">{paidLabel}</span>
                      )}
                    </div>
                  </div>

                  <h3 className="font-display text-lg font-bold tracking-tight">{t.name}</h3>
                  <p className="text-sm text-[#525252] line-clamp-2">{t.tagline}</p>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
